import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Area, AreaChart
} from "recharts";
import { useWeightUnit } from "@/lib/units";

const METRIC_FIELDS = [
  { key: "weight", label: "Weight", unit: "kg", color: "#E05A1D" },
  { key: "body_fat", label: "Body Fat", unit: "%", color: "#F59E0B" },
  { key: "chest", label: "Chest", unit: "cm", color: "#22C55E" },
  { key: "waist", label: "Waist", unit: "cm", color: "#EF4444" },
  { key: "arms", label: "Arms", unit: "cm", color: "#8B5CF6" },
  { key: "thighs", label: "Thighs", unit: "cm", color: "#06B6D4" },
  { key: "shoulders", label: "Shoulders", unit: "cm", color: "#EC4899" },
];

const ChartTooltip = ({ active, payload, label, weightLabel = "kg" }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1C1C1C] border border-[#27272A] p-3 text-sm">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => {
        const fieldUnit = METRIC_FIELDS.find(f => f.key === p.dataKey)?.unit || "";
        const unit = p.dataKey === "weight" ? weightLabel : fieldUnit;
        return (
          <p key={i} style={{ color: p.stroke || p.color }} className="font-semibold">
            {p.name}: {p.value} {unit}
          </p>
        );
      })}
    </div>
  );
};

export default function BodyMetrics({ api }) {
  const { unit: weightUnit, fromKg, toKg, label: weightLabel } = useWeightUnit();
  const METRIC_FIELDS_DYN = METRIC_FIELDS.map((f) => (f.key === "weight" ? { ...f, unit: weightLabel } : f));
  const [measurements, setMeasurements] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [activeChart, setActiveChart] = useState("weight");
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    weight: "", body_fat: "", chest: "", waist: "", arms: "", thighs: "", shoulders: "", notes: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [mRes, cRes] = await Promise.all([
        axios.get(`${api}/measurements`),
        axios.get(`${api}/measurements/chart`),
      ]);
      setMeasurements(mRes.data);
      setChartData(cRes.data.chart || []);
    } catch (e) {
      console.error("Fetch measurements error:", e);
    }
  };

  useEffect(() => { fetchData(); }, [api]);

  const saveMeasurement = async () => {
    const payload = { date: form.date, notes: form.notes };
    METRIC_FIELDS.forEach(f => {
      const val = form[f.key];
      if (val !== "" && val !== null && val !== undefined) {
        let parsed = parseFloat(val);
        if (f.key === "weight" && weightUnit === "lb") parsed = Number(toKg(parsed).toFixed(2));
        payload[f.key] = parsed;
      }
    });
    if (!payload.weight && !payload.body_fat && !payload.chest && !payload.waist && !payload.arms && !payload.thighs && !payload.shoulders) {
      toast.error("Enter at least one measurement");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${api}/measurements`, payload);
      toast.success("Measurement logged!");
      setShowForm(false);
      setForm({ date: new Date().toISOString().split("T")[0], weight: "", body_fat: "", chest: "", waist: "", arms: "", thighs: "", shoulders: "", notes: "" });
      fetchData();
    } catch (e) {
      toast.error("Failed to save measurement");
    } finally {
      setSaving(false);
    }
  };

  const deleteMeasurement = async (id) => {
    try {
      await axios.delete(`${api}/measurements/${id}`);
      toast.success("Measurement deleted");
      fetchData();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // Calculate latest vs previous for delta indicators
  const getLatestDelta = (key) => {
    const entries = measurements.filter(m => m[key] != null).slice(0, 2);
    if (entries.length < 2) return null;
    return +(entries[0][key] - entries[1][key]).toFixed(1);
  };

  const activeMetric = METRIC_FIELDS_DYN.find(f => f.key === activeChart);
  const filteredChart = chartData
    .filter(d => d[activeChart] != null)
    .map(d => activeChart === "weight"
      ? { ...d, weight: Number(fromKg(d.weight).toFixed(1)) }
      : d);

  return (
    <div className="space-y-6" data-testid="body-metrics-page">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Body Metrics
          </h2>
          <p className="text-sm text-[#71717A] mt-1">Track your body measurements and weight over time</p>
        </div>

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <button
              className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-5 py-2.5 text-sm flex items-center gap-2 transition-colors active:scale-[0.98]"
              data-testid="new-measurement-btn"
            >
              <Plus size={16} weight="bold" /> Log Measurement
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#161616] border-[#27272A] max-w-lg">
            <DialogHeader>
              <DialogTitle
                className="text-xl font-bold uppercase tracking-tight text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Log Body Measurement
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                  data-testid="measurement-date-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {METRIC_FIELDS_DYN.map(f => (
                  <div key={f.key}>
                    <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">
                      {f.label} ({f.unit})
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={form[f.key]}
                      onChange={(e) => updateField(f.key, e.target.value)}
                      className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm focus:border-[#E05A1D] outline-none"
                      placeholder={`e.g. ${f.key === 'weight' ? (weightUnit === 'lb' ? '176' : '80') : f.key === 'body_fat' ? '15' : '90'}`}
                      data-testid={`measurement-${f.key}-input`}
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-3 py-2 text-sm h-16 resize-none focus:border-[#E05A1D] outline-none"
                  placeholder="Any notes..."
                  data-testid="measurement-notes-input"
                />
              </div>

              <button
                onClick={saveMeasurement}
                disabled={saving}
                className="w-full bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide py-3 transition-colors disabled:opacity-50"
                data-testid="save-measurement-btn"
              >
                {saving ? "Saving..." : "Save Measurement"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Stats Cards */}
      {measurements.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="metric-summary-cards">
          {METRIC_FIELDS_DYN.map(f => {
            const latest = measurements.find(m => m[f.key] != null);
            const delta = getLatestDelta(f.key);
            if (!latest || latest[f.key] == null) return null;
            const displayLatest = f.key === "weight" ? fromKg(latest[f.key]).toFixed(1) : latest[f.key];
            const displayDelta = f.key === "weight" && delta !== null ? Number(fromKg(delta).toFixed(1)) : delta;
            return (
              <div key={f.key} className="bg-[#161616] border border-[#27272A] p-4" data-testid={`metric-card-${f.key}`}>
                <p className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1">{f.label}</p>
                <p className="text-2xl font-black text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {displayLatest}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-[#71717A]">{f.unit}</span>
                  {displayDelta !== null && (
                    <span className={`text-xs font-bold flex items-center gap-0.5 ${
                      displayDelta > 0 ? (f.key === 'waist' || f.key === 'body_fat' ? 'text-[#EF4444]' : 'text-[#22C55E]') :
                      displayDelta < 0 ? (f.key === 'waist' || f.key === 'body_fat' ? 'text-[#22C55E]' : 'text-[#EF4444]') :
                      'text-[#71717A]'
                    }`}>
                      {displayDelta > 0 ? <TrendUp size={12} /> : displayDelta < 0 ? <TrendDown size={12} /> : <Minus size={12} />}
                      {displayDelta > 0 ? `+${displayDelta}` : displayDelta}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Weight / Metrics Chart */}
      <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="body-chart-section">
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-lg font-semibold tracking-tight uppercase text-white"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            {activeMetric?.label || "Weight"} Over Time
          </h3>
        </div>

        {/* Metric Tabs */}
        <Tabs value={activeChart} onValueChange={setActiveChart} className="mb-4">
          <TabsList className="bg-[#0F0F0F] border border-[#27272A] h-auto flex-wrap">
            {METRIC_FIELDS_DYN.map(f => (
              <TabsTrigger
                key={f.key}
                value={f.key}
                className="text-xs uppercase tracking-wider font-bold data-[state=active]:bg-[#E05A1D] data-[state=active]:text-white px-3 py-1.5"
                data-testid={`chart-tab-${f.key}`}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {filteredChart.length > 1 ? (
          <ResponsiveContainer width="100%" height={300} data-testid="body-weight-chart">
            <AreaChart data={filteredChart}>
              <defs>
                <linearGradient id={`grad-${activeChart}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={activeMetric?.color || "#E05A1D"} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={activeMetric?.color || "#E05A1D"} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" tick={{ fill: '#71717A', fontSize: 11 }} />
              <YAxis
                tick={{ fill: '#71717A', fontSize: 11 }}
                domain={['auto', 'auto']}
              />
              <Tooltip content={<ChartTooltip weightLabel={weightLabel} />} />
              <Area
                type="monotone"
                dataKey={activeChart}
                stroke={activeMetric?.color || "#E05A1D"}
                strokeWidth={2}
                fill={`url(#grad-${activeChart})`}
                dot={{ fill: activeMetric?.color || "#E05A1D", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, stroke: activeMetric?.color || "#E05A1D", strokeWidth: 2, fill: "#0F0F0F" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[300px] text-[#71717A] text-sm border border-dashed border-[#27272A]">
            {filteredChart.length === 1
              ? "Log at least 2 measurements to see the trend"
              : `No ${activeMetric?.label.toLowerCase() || "weight"} data yet. Start logging!`
            }
          </div>
        )}
      </div>

      {/* Measurement History */}
      <div data-testid="measurement-history">
        <h3
          className="text-lg font-semibold tracking-tight uppercase text-[#A1A1AA] mb-3"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          History
        </h3>
        {measurements.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#27272A]">
            <p className="text-[#71717A] text-sm">No measurements logged yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {measurements.map((m) => (
              <div
                key={m.id}
                className="bg-[#161616] border border-[#27272A] p-4 flex items-center justify-between hover:border-[#3f3f46] transition-colors group"
                data-testid={`measurement-row-${m.id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-semibold text-white">{m.date}</span>
                    {m.notes && <span className="text-xs text-[#71717A]">{m.notes}</span>}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {METRIC_FIELDS_DYN.map(f => (
                      m[f.key] != null && (
                        <span key={f.key} className="text-xs text-[#A1A1AA]">
                          <span className="font-bold" style={{ color: f.color }}>{f.label}:</span> {f.key === "weight" ? fromKg(m[f.key]).toFixed(1) : m[f.key]} {f.unit}
                        </span>
                      )
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => deleteMeasurement(m.id)}
                  className="text-[#71717A] hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 p-2"
                  data-testid={`delete-measurement-${m.id}`}
                >
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
